import { authClient } from './auth-client.ts'
import { fetchHierarchy } from './hierarchy-api.ts'
import { listPrincipals } from './vault-api-principals.ts'

/**
 * "Which vault principal am I?", answered for the browser.
 *
 * Messaging is addressed to PRINCIPALS, not to users: the operator's own
 * human principal is what a message to Matt is addressed to, and it is
 * what the server stamps as the sender when he writes one. The admin
 * message list is root-scoped — it returns an agent's mail as readily as
 * his own — so every "is this for me" question in the UI needs this id,
 * and without it the honest answer to all of them is "I cannot tell"
 * rather than a guess.
 *
 * There is deliberately no `GET /vault/admin/{root}/principals/me`. The
 * join is `vault_principals.userId === session.user.id`, both halves of
 * which the browser can already fetch, and an endpoint whose only job is
 * a one-row lookup the client can do is an endpoint to maintain forever.
 * If the principal list ever grows past a page, that is the moment to
 * add one.
 *
 * NOTHING HERE IS A SECURITY CONTROL. The server decides what this
 * operator may read, send and mark read, on every request, from their
 * cookie. This answer only decides what the UI emphasises and what the
 * bell counts; a wrong answer makes the console unhelpful, never
 * permissive.
 */

export interface VaultViewer {
  /** The tenant root the console is operating on — the first root the
   * operator is a member of, which is the same one every other vault
   * page picks (`routes/vault.tsx`). */
  rootId: string
  /** The operator's own vault principal, or `null` when they do not have
   * one yet.
   *
   * A human principal is minted lazily, by `ensurePrincipalForUser`, the
   * first time the operator sends or marks read. So `null` is an
   * ordinary state for a new operator rather than an error, and the UI
   * says "no principal yet" instead of showing an empty inbox as if
   * nothing had arrived. */
  principalId: string | null
}

/** The session user's id, or `null`. better-auth resolves rather than
 * rejects on an auth failure, and the envelope is narrowed structurally
 * instead of cast, so a shape change surfaces as `null` rather than as a
 * runtime read of `undefined`.
 *
 * Exported because the messages route already fetches the principal list
 * for its compose form, and making it call `loadVaultViewer` as well
 * would fetch that list twice on every page load. */
export async function sessionUserId(): Promise<string | null> {
  const result: unknown = await authClient.getSession()
  if (typeof result !== 'object' || result === null || !('data' in result))
    return null
  const { data } = result
  if (typeof data !== 'object' || data === null || !('user' in data))
    return null
  const { user } = data
  if (typeof user !== 'object' || user === null || !('id' in user)) return null
  return typeof user.id === 'string' ? user.id : null
}

/**
 * The viewer's principal, picked out of a principal list the caller
 * already has.
 *
 * `kind === 'human'` as well as the user match, because a user id only
 * ever appears on a human principal and matching on it alone would be
 * one schema change away from selecting a runner.
 */
export function viewerPrincipalFrom(
  principals: readonly { id: string; kind: string; userId: string | null }[],
  userId: string | null,
): string | null {
  if (userId === null) return null
  const mine = principals.find(
    (principal) => principal.kind === 'human' && principal.userId === userId,
  )
  return mine?.id ?? null
}

/**
 * Resolves the viewer from scratch, for a caller that has nothing yet —
 * which is the notification bell, and only the bell.
 *
 * Throws on a failed fetch, because its one caller is an attention
 * source that is required to report a failed source rather than pretend
 * it saw nothing. A route loader should use `viewerPrincipalFrom`
 * against the principal list it is already fetching.
 */
export async function loadVaultViewer(): Promise<VaultViewer> {
  const [tree, userId] = await Promise.all([fetchHierarchy(), sessionUserId()])
  const rootId = tree.roots[0]?.id ?? ''
  if (rootId === '' || userId === null) return { rootId, principalId: null }
  const principals = await listPrincipals(rootId)
  return { rootId, principalId: viewerPrincipalFrom(principals, userId) }
}
